import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import logo from '../../assets/logo.png';
import { verifyOTP, clearError } from '../../store/slices/authSlice';

const OTPStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, error, isAuthenticated, user } = useSelector((state) => state.auth);

  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  // Clear error on component mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = getRedirectPath(user?.role_id);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Get redirect path based on user role
  const getRedirectPath = (u) => {
    const roleId = u?.role_id;
    switch (roleId) {
      case 1: // Super Admin
        return '/practices';
      case 2: // Admin
        return '/practices';
      case 3: // Practice Admin
        return u?.practice_id ? `/practices/${u.practice_id}` : '/practices';
      case 4: // Provider
        return '/provider-dashboard';
      case 5: // Practice Caregiver
        return '/caregiver-dashboard';
      case 6: // Patient
        return '/patient-dashboard';
      case 7: // System Caregiver
        return '/system-caregiver-dashboard';
      default:
        return '/practices';
    }
  };

  // Handle OTP input change
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      const result = await dispatch(verifyOTP({ otp }));

      if (verifyOTP.fulfilled.match(result)) {
        toast.success('OTP verified successfully!');
        if (result.payload.user) {
          const redirectPath = getRedirectPath(result.payload.user);
          navigate(redirectPath, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else if (verifyOTP.rejected.match(result)) {
        toast.error(result.payload || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  // Resend OTP
  const handleResendOTP = () => {
    // This would typically call an API to resend OTP
    toast.info('OTP resent to your email');
    setTimeLeft(300); // Reset timer
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="login-section">
      <div className="login-container">
        <div className="row login-shadow m-0">
          {/* Left Side - Image */}
          <div className="col-md-6">
            <div className="row h-100">
              <div className="login-left d-flex align-items-center justify-content-center">
                <div className="login-img">
                  <img
                    src="/assets/images/login-img.png"
                    alt="OTP Verification"
                    className="img-fluid"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - OTP Form */}
          <div className="col-md-6">
            <div className="row h-100">
              <div className="login-right">
                <div className="login-form">
                  {/* Logo */}
                  <div className="login-logo d-flex justify-content-center">
                    <img
                      src={logo}
                      alt="codexterlab Logo"
                      className="img-fluid"
                    />
                  </div>

                  {/* OTP Form */}
                  <form onSubmit={handleSubmit}>
                    <div className="text-center mb-4">
                      <h4>Two-Factor Authentication</h4>
                      <p className="text-muted">
                        Please enter the 6-digit code sent to your email address.
                      </p>
                    </div>

                    {/* OTP Input */}
                    <div className="form-group mb-4">
                      <label htmlFor="otp" className="form-label">Enter OTP</label>
                      <div className="position-relative">
                        <input
                          id="otp"
                          type="text"
                          value={otp}
                          onChange={handleOtpChange}
                          className="form-control text-center"
                          placeholder="000000"
                          required
                          maxLength="6"
                          style={{ fontSize: '24px', letterSpacing: '8px' }}
                        />
                      </div>
                    </div>

                    {/* Timer */}
                    <div className="text-center mb-3">
                      <small className="text-muted">
                        Code expires in: <strong>{formatTime(timeLeft)}</strong>
                      </small>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="alert alert-danger mb-3" role="alert">
                        {error}
                      </div>
                    )}

                    {/* Verify Button */}
                    <div className="cust-bttn mt-4 mb-3 text-center">
                      <div className="login-bttn">
                        <button
                          type="submit"
                          className="btn btn-primary w-100"
                          disabled={isLoading || timeLeft === 0}
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Verifying...
                            </>
                          ) : (
                            'Verify OTP'
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Resend OTP */}
                    <div className="text-center">
                      <p className="text-muted">
                        Didn't receive the code?{' '}
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={handleResendOTP}
                          disabled={timeLeft > 0}
                        >
                          Resend OTP
                        </button>
                      </p>
                    </div>

                    {/* Back to Login */}
                    <div className="text-center">
                      <a href="/login" className="text-decoration-none">
                        <i className="fa fa-arrow-left me-1"></i>
                        Back to Login
                      </a>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPStep;
